import { Button } from '@bpinternal/ui-kit-next'
import { cn, formatters } from '~/utils'
import { usePlanProducts } from '../hooks/usePlanProducts'
import { showPlanSelectionDialog } from './PlanSelectionDialog'
import { getOrderedPlansInfo, planOrder } from '../utils/plan'
import { Badge } from '~/elements'
import { Clock } from 'lucide-react'
import { match } from 'ts-pattern'
import { useSuspenseQuery } from '~/services'
import { useActiveSubscription } from '../hooks'

type PlanBannerProps = {
  workspaceId: string
}

export const PlanBanner = ({ workspaceId }: PlanBannerProps) => {
  const {
    data: { billingReadonly },
  } = useSuspenseQuery('workspaces_/$workspaceId_/billing/info', { workspaceId })
  const { currentPlan, currentPhaseEndDate, nextPhasePlan } = usePlanProducts(workspaceId)
  const { yearly: yearlyActiveSubscription } = useActiveSubscription(workspaceId)

  const yearlyRenewalDate =
    yearlyActiveSubscription?.current_period_end &&
    new Date(yearlyActiveSubscription.current_period_end * 1000).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })

  const isDowngradingNextPhase =
    currentPlan.cycle === 'monthly' &&
    currentPlan.legacyName !== 'enterprise' &&
    planOrder.indexOf(nextPhasePlan.legacyName) < planOrder.indexOf(currentPlan.legacyName)

  const currentPlanInfo = getOrderedPlansInfo()[currentPlan.legacyName]
  const textColor = match(currentPlan.legacyName)
    .with('community', () => 'text-gray-400')
    .with('plus', () => 'text-blue-600')
    .with('team', () => 'text-green-500')
    .with('enterprise', () => 'text-white')
    .exhaustive()

  return (
    <div
      className={cn('relative overflow-clip rounded-xl p-6', currentPlanInfo.backgroundUrl ? '' : 'bg-gray-400')}
      style={
        currentPlanInfo.backgroundUrl
          ? {
              backgroundImage: `url(${currentPlanInfo.backgroundUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: '75% 25%',
            }
          : undefined
      }
    >
      <div className="relative flex flex-col gap-4">
        <div className="text-3xl font-semibold text-white sm:text-4xl">
          <span>{currentPlanInfo.legacyName === 'enterprise' ? 'Enterprise' : currentPlanInfo.displayName}</span>{' '}
          {currentPlan.pricePerMonth !== 0 && (
            <span>
              {formatters.$centdollars(currentPlan.pricePerMonth, 0)}
              <span className="text-sm font-normal">{currentPlanInfo.priceSuffix ? ' /mo + AI Spend' : ''}</span>
            </span>
          )}
        </div>
        <div className="flex flex-col gap-4 sm:flex-row sm:justify-between">
          <div className="flex flex-row gap-2 text-sm text-white">
            {currentPlan.cycle === 'yearly' && currentPlan.legacyName !== 'enterprise' ? (
              <p>
                Billed annually
                {yearlyRenewalDate &&
                  (yearlyActiveSubscription?.cancel_at_period_end
                    ? `, cancels ${yearlyRenewalDate}`
                    : `, renews ${yearlyRenewalDate}`)}
              </p>
            ) : (
              <p>Your current plan</p>
            )}
            {isDowngradingNextPhase && currentPhaseEndDate && (
              <Badge
                className={cn(
                  'flex h-fit flex-row items-center gap-1 rounded-md bg-blue-2 px-2 py-1 text-xs',
                  textColor
                )}
              >
                <Clock className="h-3.5 w-3.5" />{' '}
                <span>
                  Valid until{' '}
                  {new Date(currentPhaseEndDate * 1000).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                  })}
                </span>
              </Badge>
            )}
          </div>
          <Button
            variant="secondary"
            disabled={billingReadonly}
            onClick={() => showPlanSelectionDialog({ workspaceId })}
          >
            Manage Plan
          </Button>
        </div>
      </div>
    </div>
  )
}
